import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppSelector } from '../hooks';
import ConfirmDeleteEntryDialog, { useConfirmDeleteEntryDialog } from '../components/ConfirmDeleteEntryDialog';
import { EntityType, Entry, PressedKey, RepositoryEntry } from '../types';
import { getRepositorySingleUnitLabel } from '../common/util';
import {
	setActiveRepositoryId,
	repositoryEntryArchiveLocalAction,
	repositoryEntryUnarchiveLocalAction,
} from './contentSlice';
import useAuth from '../components/Auth';
import { useDispatch } from 'react-redux';
import { useRepositoryEntryArchiveMutation, useRepositoryEntryUnarchiveMutation } from '../services/repositoryEntries';
import { Box, Button, CircularProgress, Container, Theme, useMediaQuery } from '@mui/material';
import { useContentNavigationEffects } from '../hooks/useContentNavigationEffects';
import { useKeyboardEffects } from '../components/KeyboardEffects';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { AddRepositoryEntryFormDialog, useRepositoryEntryDialog } from '../components/AddRepositoryEntryDialog';
import { selectIsAnyDialogOpen } from './formDataSlice';
import { Helmet } from 'react-helmet';
import config from '../common/config';
import { EntryContentSimple } from '../components/EntryContentSimple';
import { useNavigate, useParams } from 'react-router-dom';
import EditRepositoryEntryFieldDialog from '../components/EditRepositoryEntryFieldDialog';
import { EntryPropertyBelongsToBlockEditDialog } from '../components/EntryPropertyBelongsToEditDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { useRepositoryEntryData } from '../hooks/useRepositoryEntryData';
import { useRepositoryEntryEditFlow } from '../hooks/useRepositoryEntryEditFlow';
import { useSharedView } from '../hooks/useSharedView';
import NotFoundView from './NotFoundView';

export { RepositoryEntryDrawer } from './RepositoryEntryDrawer';
export { useRepositoryEntryData } from '../hooks/useRepositoryEntryData';
export { useRepositoryEntryEditFlow } from '../hooks/useRepositoryEntryEditFlow';

function RepositoryEntryView() {
	const { isAuthenticated } = useAuth();
	const { isSharedView, toPath } = useSharedView();
	const dispatch = useDispatch();
	const { id: routeEntryId } = useParams();
	const goBack = useBackNavigation();
	const navigate = useNavigate();
	const isAnyDialogOpen = useAppSelector(selectIsAnyDialogOpen);
	const repositoryEntryDialog = useRepositoryEntryDialog();

	const repositoryEntryId = routeEntryId ?? null;
	const documentEnabled = Boolean(repositoryEntryId);

	const documentData = useRepositoryEntryData(documentEnabled, repositoryEntryId);

	const { populatedEntry: populatedRepositoryEntry, repository, propertyDefinitions: repositoryPropertyDefinitions } =
		documentData;

	useContentNavigationEffects();

	const setKeyboardMappings = useKeyboardEffects();

	const { isConfirmDeleteDialogOpen, confirmDeleteDialogEntry, closeConfirmDeleteDialog, submitConfirmDeleteDialog, openConfirmDeleteDialog } =
		useConfirmDeleteEntryDialog();
	const [repositoryEntryArchiveMutation] = useRepositoryEntryArchiveMutation();
	const [repositoryEntryUnarchiveMutation] = useRepositoryEntryUnarchiveMutation();

	const repositoryEntryEditFlow = useRepositoryEntryEditFlow({
		activeEntry: populatedRepositoryEntry,
		repositoryEntryDialog,
		onNavigateToEntry: (entryId) => navigate(toPath(`/entries/${entryId}`)),
		resetKey: routeEntryId,
		isAnyDialogOpen,
	});

	const handleDeleteEntryClick = useCallback(() => {
		if (!populatedRepositoryEntry) {
			return;
		}
		openConfirmDeleteDialog({ ...populatedRepositoryEntry, type: EntityType.RepositoryEntry, repositoryId: repository?.id } as { id: string; type: EntityType.RepositoryEntry; repositoryId: string } as Entry);
	}, [populatedRepositoryEntry, openConfirmDeleteDialog, repository?.id]);

	const handleArchiveEntryClick = useCallback(() => {
		if (!populatedRepositoryEntry || !repository?.id) {
			return;
		}
		if (isAuthenticated) {
			repositoryEntryArchiveMutation({
				entryId: populatedRepositoryEntry.id as string,
				repositoryId: repository.id,
			});
		} else {
			dispatch(repositoryEntryArchiveLocalAction(populatedRepositoryEntry.id as string));
		}
	}, [populatedRepositoryEntry, repository?.id, isAuthenticated, dispatch, repositoryEntryArchiveMutation]);

	const handleUnarchiveEntryClick = useCallback(() => {
		if (!populatedRepositoryEntry || !repository?.id) {
			return;
		}
		if (isAuthenticated) {
			repositoryEntryUnarchiveMutation({
				entryId: populatedRepositoryEntry.id as string,
				repositoryId: repository.id,
			});
		} else {
			dispatch(repositoryEntryUnarchiveLocalAction(populatedRepositoryEntry.id as string));
		}
	}, [populatedRepositoryEntry, repository?.id, isAuthenticated, dispatch, repositoryEntryUnarchiveMutation]);

	const handleKeyPressEscape = useCallback(() => {
		if (repositoryEntryEditFlow.anyBlockingDialog) return;

		goBack(toPath(`/collection/${repository?.id}`));
	}, [repositoryEntryEditFlow.anyBlockingDialog, repository?.id, goBack, toPath]);

	useEffect(() => {
		setKeyboardMappings({
			[PressedKey.Escape]: handleKeyPressEscape,
		});
		return () => {
			setKeyboardMappings(null);
		};
	}, [handleKeyPressEscape, setKeyboardMappings]);

	const confirmDeleteDialogTitle = useMemo(
		() => (confirmDeleteDialogEntry && repository ? `Delete ${getRepositorySingleUnitLabel(repository).toLowerCase()}?` : ''),
		[confirmDeleteDialogEntry, repository],
	);

	const confirmDeleteEntryLabel = useMemo(() => {
		if (!confirmDeleteDialogEntry || !populatedRepositoryEntry) {
			return '';
		}
		return confirmDeleteDialogEntry.id === populatedRepositoryEntry.id ? populatedRepositoryEntry.label : '';
	}, [confirmDeleteDialogEntry, populatedRepositoryEntry]);

	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const showLoading = !documentEnabled || !populatedRepositoryEntry;

	useEffect(() => {
		if (repository?.id) {
			dispatch(setActiveRepositoryId(repository.id));
		}
	}, [dispatch, repository?.id]);

	if (documentData.isNotFound) {
		return <NotFoundView />;
	}

	if (showLoading) {
		return (
			<Box flex={1} display="flex" justifyContent="center" alignItems="center">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<>
			{repository && (
				<Helmet>
					<title>
						{repository.title} - {config.appName}
					</title>
				</Helmet>
			)}
			<Box minHeight="calc(100dvh - 72px)" display="flex" flexDirection="column">
				<Container maxWidth="md" sx={{ mt: 4, mb: 8 }} disableGutters component={Box} flex={1}>
					<EntryContentSimple
						disableTabIndex={false}
						disableMetaFieldEdit={isAuthenticated}
						readOnly={isSharedView}
						populatedEntry={populatedRepositoryEntry}
						propertyDefinitions={repositoryPropertyDefinitions}
						onRelationDialogOpen={repositoryEntryEditFlow.onRelationDialogOpen}
						onScalarFieldEdit={repositoryEntryEditFlow.onScalarFieldEdit}
					/>
				</Container>
				{!isSharedView && <Container maxWidth={isSmScreen ? 'sm' : false}>
					<Box display="flex" flexDirection="column" alignItems="flex-end" gap={2} mb={isSmScreen ? 10 : 5}>
						<Button
							aria-label={populatedRepositoryEntry.isArchived ? 'Unarchive entry' : 'Archive entry'}
							sx={{ color: 'text.secondary' }}
							disabled={repositoryEntryEditFlow.anyBlockingDialog}
							onClick={populatedRepositoryEntry.isArchived ? handleUnarchiveEntryClick : handleArchiveEntryClick}
							endIcon={populatedRepositoryEntry.isArchived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
						>
							{populatedRepositoryEntry.isArchived ? 'Unarchive' : 'Archive'}
						</Button>

						<Button
							aria-label="Delete entry"
							color="error"
							disabled={repositoryEntryEditFlow.anyBlockingDialog}
							onClick={handleDeleteEntryClick}
							endIcon={<DeleteIcon fontSize="small" />}
						>
							Delete
						</Button>
					</Box>
				</Container>}
			</Box>
			<EditRepositoryEntryFieldDialog {...repositoryEntryEditFlow.editFieldDialogProps} />
			{repositoryEntryEditFlow.relationDialogProps && (
				<EntryPropertyBelongsToBlockEditDialog {...repositoryEntryEditFlow.relationDialogProps} />
			)}
			<AddRepositoryEntryFormDialog
				open={repositoryEntryDialog.isAddRepositoryEntryDialogOpen}
				repositoryId={repositoryEntryDialog.addRepositoryEntryDialog?.repositoryId ?? null}
				linkToProperty={repositoryEntryDialog.addRepositoryEntryDialog?.linkToProperty}
				linkToEntryId={repositoryEntryDialog.addRepositoryEntryDialog?.linkToEntryId}
				onCreated={(created) => {
					repositoryEntryEditFlow.notifyAddRepositoryEntryCreated(created);
				}}
				onClose={repositoryEntryEditFlow.closeAddRepositoryEntryDialogWithCleanup}
			/>
			<ConfirmDeleteEntryDialog
				open={isConfirmDeleteDialogOpen}
				entry={confirmDeleteDialogEntry}
				handleClose={closeConfirmDeleteDialog}
				handleSubmit={() => {
					const rid = (confirmDeleteDialogEntry as RepositoryEntry)?.repositoryId;
					submitConfirmDeleteDialog({
						redirectPath: rid ? `/collection/${rid}` : '/',
						repositoryId: rid,
					});
				}}
				title={confirmDeleteDialogTitle}
				dialogContent={confirmDeleteEntryLabel}
			/>
		</>
	);
}

export default RepositoryEntryView;
